<script>
// TODO: переписать на Fragment с телепортом popover (в body) после перехода на vue3
// TODO: общее взаимодействие с tooltip - найти точки соприкосновения

import { computePosition, flip, offset, shift, size } from '@floating-ui/dom';

import { useClickOutside } from '@/composables/useClickOutside';
import { useCloseEscape } from '@/composables/useCloseEscape';
import { useId } from '@/composables/useId';
import { useTrapFocus } from '@/composables/useTrapFocus';

export default defineComponent({

  name: 'BaseMenu',

  inheritAttrs: false,

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    placement: {
      /** @type import('vue').PropType<import('@floating-ui/utils').Placement> */
      type: String,
      default: 'bottom-start',
    },
    popoverClass: {
      type: [String, Object, Array],
      default: undefined,
    },
    trapFocus: {
      type: Boolean,
      default: true,
    },
    popoverDisabled: {
      type: Boolean,
      default: false
    },
    minWidthFromActivator: {
      type: Boolean,
      default: false,
    },
    disableActivatorListener: {
      type: Boolean,
      default: false,
    },
    enableOverflowFlip: {
      type: Boolean,
      default: false,
    },
    offset: {
      type: Number,
      default: 6,
    },
  },

  emits: ['input', 'update:value'],

  setup(props, { attrs, emit, expose }) {
    const activatorElementRef = shallowRef();
    const popoverSlotRef = shallowRef();
    const isPopoverVisible = ref(props.value ?? false);
    const popoverFloatPosition = reactive({ x: 0, y: 0, multiplierY: 1, minWidth: '0px', maxWidth: 'unset', maxHeight: '300px' });
    const id = computed(() => attrs.id || useId('menu'));

    const { set: setTrapFocus, unset: removeTrapFocus } = useTrapFocus();
    const { startListen: listenClickOutside, stopListen: pauseListenClickOutside } = useClickOutside(popoverSlotRef, () => hidePopover(), { capture: true, ignore: [activatorElementRef] });
    const { startListen: listenCloseEscape, stopListen: pauseListenCloseEscape } = useCloseEscape(() => hidePopover(), {
      canHandleEvent(event) {
        return event.composedPath().includes(activatorElementRef.value);
      }
    });

    if (!props.value) {
      pauseListenClickOutside();
      pauseListenCloseEscape();
    }

    watch(
      () => props.value,
      (newValue) => {
        if (newValue === isPopoverVisible.value)
          return;

        if (newValue) {
          showPopoverByEvent(null);
        }
        else {
          hidePopover();
        }
      }
    );

    function setActivatorSlotRef(value) {
      const element = value?.$el || value;

      if (!props.disableActivatorListener && value && value !== activatorElementRef.value) {
        element.addEventListener('click', showPopoverByEvent);

        if (activatorElementRef.value) {
          activatorElementRef.value.removeEventListener('click', showPopoverByEvent);
        }
      }

      activatorElementRef.value = element;
    }

    function showPopoverByEvent(event) {
      event?.stopPropagation();

      if (isPopoverVisible.value) {
        hidePopover();
        return;
      }

      if (props.popoverDisabled)
        return;

      isPopoverVisible.value = true;

      nextTick().then(() => {
        listenCloseEscape();
        listenClickOutside();

        if (props.trapFocus) {
          setTrapFocus([popoverSlotRef.value, activatorElementRef.value]);
        }

        update();
      });

      emit('input', true);
      emit('update:value', true);
    }

    function hidePopover() {
      pauseListenClickOutside();
      pauseListenCloseEscape();
      removeTrapFocus();

      isPopoverVisible.value = false;

      emit('input', false);
      emit('update:value', false);
    }

    function update() {
      if (!isPopoverVisible.value)
        return;

      if (!activatorElementRef.value || !popoverSlotRef.value)
        return;

      const middleware = [
        flip({ fallbackAxisSideDirection: 'start', crossAxis: false }),
        shift(),
        offset(props.offset),
        size({
          apply: ({ rects, availableHeight }) => {
            if (props.minWidthFromActivator) {
              popoverFloatPosition.minWidth = `${rects.reference.width}px`;
            }

            popoverFloatPosition.maxHeight = `${Math.max(300, availableHeight)}px`;
          },
        }),
      ];
      if (!props.enableOverflowFlip) {
        middleware.shift(); // means Array's shift()
      }
      computePosition(activatorElementRef.value, popoverSlotRef.value, {
        placement: props.placement,
        middleware,
      }).then(({ x, y, placement }) => {
        // NOTE: странный баг с отрисовкой через trasform (на Chrome): если давать дробные числа, то текст начинает блюриться и будто находится в расфокусе. С целыми числами такое не воспроизводится - форсируем
        popoverFloatPosition.x = `${Math.round(x)}px`;
        popoverFloatPosition.y = `${Math.round(y)}px`;

        popoverFloatPosition.multiplierY = placement.includes('top') ? 1 : -1;
      });
    }

    onMounted(() => {
      if (!activatorElementRef.value)
        return;

      const resizeObserver = new ResizeObserver(() => {
        update();
      });

      resizeObserver.observe(activatorElementRef.value);
    });

    expose({
      activatorElementRef,
    });

    return {
      id,

      setActivatorSlotRef,
      popoverSlotRef,

      hidePopover,

      popoverFloatPosition,

      isPopoverVisible,
    };
  },
});
</script>

<template>
  <div data-menu-container :data-expanded="isPopoverVisible" :class="$style.container" v-bind="$attrs">
    <slot
      :a11y-attributes="{
        'id': `${id}-activator`,
        'aria-controls': id,
        'aria-expanded': isPopoverVisible ? 'true' : 'false',
        'aria-haspopup': 'menu',
      }"
      name="activator"
      :set-ref="setActivatorSlotRef"
      :popover-visible="isPopoverVisible"
      :close="hidePopover"
      :activator-class="$style.activator"
    />

    <Transition :enter-active-class="$style['fade-in']" :leave-active-class="$style['fade-out']">
      <div
        v-if="isPopoverVisible"
        :id="id"
        ref="popoverSlotRef"
        role="menu"
        :aria-hidden="isPopoverVisible ? 'false' : 'true'"
        :class="[$style.menu, popoverClass]"
        :style="{
          '--menu-x-pos': popoverFloatPosition.x,
          '--menu-y-pos': popoverFloatPosition.y,
          '--menu-multiplier-y': popoverFloatPosition.multiplierY,
          '--menu-min-width': popoverFloatPosition.minWidth,
          '--menu-max-width': popoverFloatPosition.maxWidth,
          '--menu-max-height': popoverFloatPosition.maxHeight,
        }"
      >
        <slot name="popover-content" :close="hidePopover" />
      </div>
    </Transition>
  </div>
</template>

<style lang="css" module>
@keyframes fade {
  from {
    opacity: 0;
    transform: translate(var(--menu-x-pos), calc(var(--menu-y-pos) + calc(var(--menu-multiplier-y) * min(15%, 12px)))) scale(0.9);
  }

  to {
    opacity: 1;
  }
}

.container {
  display: inline-block;
  position: relative;
}

.container[data-expanded=true] {
  z-index: 119;
}

.activator {
  /* position: relative;
  z-index: 121; */
}

.menu {
  min-width: var(--menu-min-width);
  max-width: var(--menu-max-width);
  max-height: var(--menu-max-height);
  position: absolute;
  top: 0;
  left: 0;

  overflow-y: auto;

  background: #fff;
  box-shadow: 0px 4px 24px 0px rgba(53, 57, 73, 0.12);

  border-radius: 12px;

  z-index: 120;
}

.menu[aria-hidden='false'] {
  transform: translate(var(--menu-x-pos), var(--menu-y-pos));
}

@media (prefers-reduced-motion: no-preference) {
  .fade-in {
    animation: fade 150ms ease-in;
  }

  .fade-out {
    animation: fade 150ms ease-out;
    animation-direction: reverse;
  }
}
</style>
